import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Printer, ArrowLeft } from "@phosphor-icons/react";
import { toast } from "sonner";

const fmt = (n) => (n ?? 0).toLocaleString("id-ID", { maximumFractionDigits: 2 });

export default function SPBPrintPage() {
  const { id } = useParams();
  const [spb, setSpb] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get(`/receiving/${id}/spb`).then((r) => setSpb(r.data))
      .catch((e) => {
        const msg = formatApiErrorDetail(e.response?.data?.detail) || e.message;
        setErr(msg); toast.error(msg);
      });
  }, [id]);

  if (err) return <div className="p-10 text-center text-red-700">{err}</div>;
  if (!spb) return <div className="p-10 text-center text-slate-500">Memuat SPB…</div>;

  const lab = spb.lab_result || {};
  return (
    <div className="min-h-screen bg-slate-100 py-8 print:bg-white print:py-0">
      <div className="max-w-3xl mx-auto px-4 print:px-0 print:max-w-none">
        {/* Toolbar (hidden on print) */}
        <div className="flex items-center justify-between mb-4 print:hidden">
          <Button variant="outline" onClick={() => window.history.back()} className="gap-2">
            <ArrowLeft size={15} weight="bold" /> Kembali
          </Button>
          <Button onClick={() => window.print()} data-testid="spb-print-btn" className="bg-slate-900 hover:bg-slate-800 text-white gap-2">
            <Printer size={15} weight="bold" /> Cetak SPB
          </Button>
        </div>

        {/* SPB document */}
        <div id="spb-doc" className="bg-white shadow-md print:shadow-none border border-slate-200 print:border-0 p-8 print:p-6" data-testid="spb-document">
          {/* Header */}
          <div className="flex items-start gap-4 pb-4 border-b-2 border-slate-900">
            <img src="/primergy-logo.jpeg" alt="Primergy" className="w-16 h-16 rounded object-cover" />
            <div className="flex-1">
              <div className="text-[10px] font-bold tracking-widest text-slate-500 uppercase">PT Primergy Solution</div>
              <div className="font-heading font-black text-2xl text-slate-900 leading-tight">SURAT PERINTAH BONGKAR</div>
              <div className="text-[11px] text-slate-600 mt-0.5">Dokumen operasional plant — arsip 3 tahun</div>
            </div>
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">No. SPB</div>
              <div className="font-mono font-bold text-slate-900 text-base">{spb.spb_no}</div>
              <div className="text-[10px] text-slate-500 font-mono mt-1">
                {spb.spb_date ? new Date(spb.spb_date).toLocaleString("id-ID", { dateStyle: "long", timeStyle: "short" }) : "-"}
              </div>
            </div>
          </div>

          {/* Section 1: Referensi */}
          <Section title="Referensi Penerimaan">
            <Row label="No. Referensi" value={spb.ref_no} mono />
            <Row label="Supplier" value={spb.supplier} />
            <Row label="Jenis Bahan" value={spb.material} />
            <Row label="Estimasi Qty" value={`${fmt(spb.estimated_qty_liter)} Liter`} mono />
            <Row label="Tangki Tujuan" value={spb.destination_tank || "—"} />
          </Section>

          {/* Section 2: Kendaraan */}
          <Section title="Detail Kendaraan">
            <Row label="No. Plat" value={spb.truck_no} mono />
            <Row label="Driver" value={spb.driver} />
            <Row label="Gross In (Timbang Masuk)" value={`${fmt(spb.gross_in)} kg`} mono strong />
          </Section>

          {/* Section 3: Hasil Lab */}
          <Section title="Hasil Uji Laboratorium">
            <Row label="Density" value={(lab.density_used || lab.density)?.toFixed?.(3) || "—"} mono />
            <Row label="Yield Rate" value={`${lab.yield_rate ?? "—"} %`} mono />
            <Row label="Bensin" value={`${lab.bensin ?? "—"} %`} mono />
            {lab.air !== undefined && <Row label="Kadar Air" value={`${lab.air} %`} mono />}
            {lab.notes && <Row label="Catatan Lab" value={lab.notes} />}
            <Row label="Petugas Lab" value={lab.submitted_by_name || lab.submitted_by || "—"} />
          </Section>

          {spb.notes && (
            <div className="mt-4 border border-slate-200 bg-slate-50 p-3 rounded text-[12px]">
              <b>Catatan Tambahan:</b> {spb.notes}
            </div>
          )}

          {/* Signatures */}
          <div className="mt-8 pt-4 border-t border-slate-200 grid grid-cols-3 gap-6 text-center">
            <SigBlock label="Admin Pabrik" name={spb.created_by || "—"} />
            <SigBlock label="Approver (Pengawas Pabrik)" name={spb.spb_approved_by_name || spb.spb_approved_by || "—"} />
            <SigBlock label="Driver Pengirim" name={spb.driver} />
          </div>

          <div className="mt-6 text-[10px] text-slate-500 text-center font-mono print:mt-4">
            Dokumen ini sah tanpa cap perusahaan bila diterbitkan melalui sistem Primergy Plant Management.
          </div>
        </div>
      </div>

      {/* Print stylesheet */}
      <style>{`
        @media print {
          @page { size: A4; margin: 15mm; }
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        }
      `}</style>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="mt-5">
      <div className="text-[10px] font-bold uppercase tracking-widest text-sky-700 mb-2">{title}</div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-1.5 gap-x-6 text-[12.5px]">{children}</div>
    </div>
  );
}

function Row({ label, value, mono, strong }) {
  return (
    <div className="flex justify-between border-b border-dotted border-slate-200 py-1">
      <span className="text-slate-600">{label}</span>
      <span className={`text-slate-900 text-right ${mono ? "font-mono" : ""} ${strong ? "font-bold" : ""}`}>{value ?? "—"}</span>
    </div>
  );
}

function SigBlock({ label, name }) {
  return (
    <div>
      <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">{label}</div>
      <div className="border-b border-slate-800 h-16" />
      <div className="text-[11.5px] text-slate-800 font-semibold mt-1.5">( {name || "—"} )</div>
    </div>
  );
}
